import { ReactNode } from "react";
import { TypographyProps } from "@mui/material";
import { SvgIconProps } from "@mui/material/SvgIcon/SvgIcon";

// Base

export interface BaseModalProps {
  isOpen: boolean;
  onClose: VoidFunction;
}

// Common

export interface SelectOption<T extends string | number = string> {
  label: string;
  value: T;
}

export interface TabsOption<TabsValue extends string = string> {
  label: string;
  value: TabsValue;
  disabled?: boolean;
  isVisible?: boolean;
  component: ReactNode;
  urlAnchor?: `#${TabsValue}`;
}

export interface Pagination {
  page: number;
  pageSize: number;
  total: number;
}

// Enums

export enum PageTypes {
  VendorOpportunities = "vo",
  CreditSubmission = "cs",
}

export enum ContentViewType {
  Desktop = "Desktop",
  Mobile = "Mobile",
}

// Utils

export type Nullable<T> = null | T;

export type ValueOf<T, K extends keyof T> = T[K];

type OnlyPrimitiveObjectFields<
  Object extends Record<string, any>,
  Restriction extends string | number | boolean,
> = Pick<Object, { [Key in keyof Object]: Object[Key] extends Restriction ? Key : never }[keyof Object]>;

type OnlyPrimitiveObjectKeys<Object extends Record<string, any>, Restriction extends string | number | boolean> = {
  [Key in keyof Object]: Object[Key] extends Restriction ? Key : never;
}[keyof Object];

export enum PrescreenTypes {
  Prescreen = "Prescreen",
  LenderWaterfall = "LenderWaterfall",
  Demo = "Demo",
  Off = "Off",
}

export enum ModalsKeys {
  Load = "load",
  CreateEquipment = "createEquipment",
  Confirmation = "confirmation",
  FinanceForm = "financeFormDialog",
  PreviewFinanceForm = "previewFinanceForm",
  ProposalProductsDialog = "proposalProductsDialog",
  ProposalMenuOptionDetailsDialog = "proposalMenuOptionDetailsDialog",
  ProposalContractDetailsDialog = "proposalContractDetailsDialog",
  ProposalCreationDialog = "proposalCreationDialog",

  DeskingConfirmSaveDraftDialog = "confirmSaveDraftDialog",
  DeskingNotifyAboutRemovedProductsDialog = "deskingNotifyAboutRemovedProductsDialog",

  ProductCatalogDialog = "productCatalogDialog",

  ConstructorPricingInfoDialog = "constructorPricingInfoDialog",

  AftermarketAddEditMenuOptionDialog = "addEditMenuOptionDialog",
  AftermarketSaveAsDialog = "aftermarketSaveAsDialog",
  AftermarketShareMenuConfirmationDialog = "aftermarketShareMenuConfirmationDialog",

  AftermarketPartnerAddDealerDialog = "aftermarketPartnerAddDealerDialog",
  AftermarketPartnerIntegrationSettingsDialog = "aftermarketPartnerIntegrationSettingsDialog",

  PrescreenProgramsDialog = "programsDialog",

  CreateUpdateInvoiceDialog = "createUpdateInvoiceDialog",

  CorporateGuarantorDialog = "corporateGuarantorDialog",
  CreateUpdateEquipmentDialog = "createUpdateEquipmentDialog",

  InsuranceSelectSubmissionDialog = "insuranceSelectSubmissionDialog",
  InsuranceAddEditBrokerDialog = "insuranceAddEditBrokerDialog",

  SendInsuranceQuoteDialog = "sendInsuranceQuoteDialog",

  AddEditDeclineReasonDialog = "addEditDeclineReasonDialog",

  SyncDealInfoDialog = "syncDealInfoDialog",
}

// Redux

type InferValueTypes<T> = T extends { [key: string]: infer U } ? U : never;
export type ActionTypes<T extends { [key: string]: (...args: any) => any }> = ReturnType<InferValueTypes<T>>;

// Components

export type Variants = "info" | "warning" | "error" | "notification";

export interface FieldNotify {
  hide?: boolean;
  message: string;
  variant?: Variants;
  withIcon?: boolean;
  iconTooltip?: string;
  iconProps?: SvgIconProps;
  typographyProps?: TypographyProps;
}
